/* eslint-disable no-bitwise */
import { AxiosError } from 'axios';
import { RefObject, useEffect, useRef } from 'react';
import { IWidgetContext } from '~/sharedInterfaces/IState';
import { Language, Market } from '~/sharedInterfaces/SharedEnums';
import {
  IAvailableMarkets,
  IClassNames,
  ISettings,
  ITransactionEndpointData,
} from '~/sharedInterfaces/SharedInterfaces';
import { ICombinedTranslations } from '~/translations';

const env = process.env.NODE_ENV === 'development' ? 'test' : process.env.NODE_ENV;

const API_BASE_URL_DEV = `https://apig.${env}.qred.com/`;
const API_BASE_URL_PROD = 'https://apig.qred.com/';

const SIGNUP_CARD_PATH = 'widget-api/signup/card';
const SIGNUP_PATH = 'widget-api/signup';
const UPLOAD_PATH = 'widget-api/upload';
const ONBOARDING_PATH = 'widget-api/additional-info';
const COMPANY_LOOKUP_PATH = 'widget-api/company-lookup';
const HOME_ADDRESS_LOOKUP_PATH = 'widget-api/address-lookup';

export const getUrls = () => {
  return env === 'production'
    ? {
        SIGNUP_URL: `${API_BASE_URL_PROD}${SIGNUP_PATH}`,
        UPLOAD_URL: `${API_BASE_URL_PROD}${UPLOAD_PATH}`,
        ONBOARDING_URL: `${API_BASE_URL_PROD}${ONBOARDING_PATH}`,
        COMPANY_LOOKUP_URL: `${API_BASE_URL_PROD}${COMPANY_LOOKUP_PATH}`,
        HOME_ADDRESS_LOOKUP_PATH: `${API_BASE_URL_PROD}${HOME_ADDRESS_LOOKUP_PATH}`,
        KOSMA_TOKEN_URL: (market: Market, language: Language) =>
          `${API_BASE_URL_PROD}kosma/country/${market}/accounts/initflow?language=${language}`,
        SIGNUP_CARD_PATH: `${API_BASE_URL_PROD}${SIGNUP_CARD_PATH}`,
        KOSMA_SEND_TRANSACTION: (data: ITransactionEndpointData) =>
          `${API_BASE_URL_PROD}kosma/country/${data.market}/companyNumber/${data.orgNumber}/transactions?sessionId=${data.sessionId}&flowId=${data.flowId}&clientId=${data.clientId}`,
      }
    : {
        SIGNUP_URL: `${API_BASE_URL_DEV}${SIGNUP_PATH}`,
        UPLOAD_URL: `${API_BASE_URL_DEV}${UPLOAD_PATH}`,
        ONBOARDING_URL: `${API_BASE_URL_DEV}${ONBOARDING_PATH}`,
        COMPANY_LOOKUP_URL: `${API_BASE_URL_DEV}${COMPANY_LOOKUP_PATH}`,
        HOME_ADDRESS_LOOKUP_PATH: `${API_BASE_URL_DEV}${HOME_ADDRESS_LOOKUP_PATH}`,
        SIGNUP_CARD_PATH: `${API_BASE_URL_DEV}${SIGNUP_CARD_PATH}`,
        KOSMA_TOKEN_URL: (market: Market, language: Language) =>
          `https://apig-test.qred.com/kosma/country/${market}/accounts/initflow?language=${language}`,
        KOSMA_SEND_TRANSACTION: (data: ITransactionEndpointData) =>
          `https://apig-test.qred.com/kosma/country/${data.market}/companyNumber/${data.orgNumber}/transactions?sessionId=${data.sessionId}&flowId=${data.flowId}&clientId=${data.clientId}`,
      };
};

export const objectHasData = (object: {}) =>
  Object.values(object).filter((value) => value && value !== '').length > 0;

// Not used at the moment but could be useful later on
export const getUrlParam = (paramKey: string) => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get(paramKey);
};

export const isAxiosError = (error: Error | AxiosError): error is AxiosError => {
  return (error as AxiosError).isAxiosError;
};

export const useEventListener = (
  eventName: string,
  handler: Function,
  element?: RefObject<any>
) => {
  // Create a ref that stores handler
  const savedHandler = useRef<Function>();
  useEffect(() => {
    // Define the listening target
    const targetElement: any | Window = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return () => {};
    }
    // Update saved handler if necessary
    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }
    // Create event listener that calls handler function stored in ref
    const eventListener = (event: Event) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!savedHandler?.current) {
        savedHandler.current(event);
      }
    };
    targetElement.addEventListener(eventName, eventListener);
    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler]);
};

export const useInterval = (callback: any, delay: number | null) => {
  const savedCallback = useRef<() => {}>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    const tick = () => {
      if (savedCallback && savedCallback.current) {
        savedCallback.current();
      }
    };
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return () => {};
  }, [delay]);
};

export const getCurrency = (market: keyof IAvailableMarkets<any>) => {
  return market === 'se' || market === 'dk' || market === 'no' ? 'kr' : '€';
};

export const normalizeIdentifier = (identifier: string) => {
  const charactersToRemove = /[-]|[ ]|[,]/g;
  const matches = identifier.match(charactersToRemove);

  if (!matches) {
    return identifier;
  }

  let returnedIdentifier = identifier;
  matches.forEach((value) => {
    returnedIdentifier = returnedIdentifier.replace(value, '');
  });

  return returnedIdentifier;
};

export const getLocale = (settings: ISettings) =>
  `${settings.language.toLowerCase()}-${settings.market.toUpperCase()}` as keyof ICombinedTranslations;

export const addReferrerUrl = () => {
  return sessionStorage.getItem('referrerUrl') !== null
    ? sessionStorage.getItem('referrerUrl')
    : document.referrer;
};

export const addSourceToUrl = (): string => {
  const url = new URL(
    sessionStorage.getItem('landingUrl')
      ? (sessionStorage.getItem('landingUrl') as string)
      : window.location.href
  );

  url.searchParams.set('qred_source', 'widget');
  return url.toString();
};

export const isFinalPage = (widgetContext: IWidgetContext) => {
  return widgetContext.currentPage >= widgetContext.pages.length - 1;
};

export const hashCode = (str: string) => {
  // eslint-disable-next-line no-bitwise
  return `qred-${Math.abs(
    str.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0)
  )}`;
};

export const getInputClassNames = ({
  type,
  tooltip,
  error,
  loading,
  disabled,
  isValid,
  componentName,
  additionalClassName,
}: IClassNames) => {
  const classNames = ['qred-component'];

  if (type) {
    classNames.push(`qred-${type === 'checkbox' ? 'checkbox' : 'textbox'}`);
  }

  /**
   * By default to show the tooltip. So if the value is undefined we show the tooltip.
   * If tooltip is explicitly set to false (in the config), then we will not show it.
   */
  if (tooltip !== false) classNames.push('qred-tooltip');
  if (error) classNames.push('qred-error');
  if (loading) classNames.push('qred-loading');
  if (disabled) classNames.push('qred-disabled');
  if (isValid) classNames.push('qred-valid');
  if (additionalClassName) classNames.push(additionalClassName);
  classNames.push(`qred-${componentName.toLowerCase()}`);

  return classNames.join(' ');
};
